import { useEffect, useRef, useState, FunctionComponent } from 'react'
import { get } from 'libraries/lodash'
import cx from 'classnames'
import ellipsis from 'text-ellipsis'
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react'
import IconButton from '@mui/material/IconButton'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import { updateHistoryState } from 'libraries/front-end-core/core/history'
import { scrollTo } from 'libraries/front-end-core/helpers/document'
import TealToolTip from 'components/TealToolTip'
import Selector from 'components/accordion/Selector'
import { AccordionHeaderProps, toDataProps } from 'components/accordion/AccordionHeader'
import { CURRENT_AWARENESS, LEGISLATION } from 'constants/controllers'
import HoverIconButtonContainer from 'redux/containers/components/accordion/HoverIconButtonContainer'
import { GroupHeaderContainerProps } from 'redux/containers/components/accordion/GroupHeaderContainer'
import { GroupProps } from './Group'
import { AccordionGroup } from 'interfaces/accordion'
import ComingSoonDialog from 'components/ComingSoonDialog'
import { comingSoonText } from 'helpers/coming-soon'
import AccordionHeaderToolTip from './AccordionHeaderToolTip'

// `expanded`/`selected` are `any` because callers pass either a boolean or a
// 0/1 number depending on which service built the node (see AccordionHeaderProps) -
// Boolean(...) normalises either representation to a real boolean.
const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value !== ''

const GroupHeader: FunctionComponent<GroupHeaderProps> = ({
  aol,
  atBottom,
  className,
  depth,
  eventCategory,
  expanded,
  id,
  nodeId,
  focusedId,
  selected,
  prefix = '',
  suffix = '',
  isArchived,
  comingSoon = false,
  group,
  allContentExpanded,
  setAllContentExpanded,
  cxNames = {},
  showAllS1,
  title = '',
  parentTitle,
  urlNode,
  setAccordionUrlNode,
  controller,
  isMobile,
  pageId,
  hasAccess
}) => {
  const [showComingSoonDialog, setShowComingSoonDialog] = useState(false)
  const depthStyle = get(cxNames, 'groupHeader.depthStyle', (): any => null)
  const iconDepthStyle = get(cxNames, 'groupHeader.iconDepthStyle', (): any => null)
  const [isUrlOwned, setIsUrlOwned] = useState(false)
  const scrollToRef = useRef<HTMLDivElement>(null)

  const isExpanded = Boolean(expanded)
  const isSelected = Boolean(selected)
  const hasTitle = isNonEmptyString(title)
  const hasParentTitle = isNonEmptyString(parentTitle)
  const accessDenied = hasAccess === false

  const Prefix = isNonEmptyString(prefix) ? <GroupHeaderPrefix prefix={prefix} /> : null
  const Suffix = suffix !== '' ? <span className='fw5 nowrap'>{suffix}</span> : null
  const childProps = Object.assign(
    { className },
    toDataProps({
      depth,
      expanded: Number(isExpanded),
      id,
      nodeId,
      prefix,
      selected,
      suffix,
      title,
      type: 'group',
      isArchived
    })
  )

  const labelPaddingStyle = (depth: number): string => depth > 1 ? `0rem 1rem 0rem ${depth}.75rem` : '0.75rem 1rem'

  const showToggleExpandAllIcon = (isExpanded && depth > 1) && Array.isArray(group)
    ? (group).filter((node) => node.nodeType === 'content').length > 0
    : false

  const noPointerEvents = (controller === LEGISLATION && depth === 2)

  const handleOnClick = (): void => {
    if (comingSoon) {
      setShowComingSoonDialog(true)
      return
    }
    if (nodeId !== '' && !noPointerEvents) {
      const url = nodeId
      if (controller !== CURRENT_AWARENESS) {
        setAccordionUrlNode(url)
        history.pushState({}, '', url)
        updateHistoryState({ viewName: controller })
      }
    }
  }

  useEffect(() => {
    if (isUrlOwned && isExpanded) {
      scrollTo({ pageId: pageId ?? '', element: scrollToRef.current as any, animate: true, useWindowScrollTo: isMobile })
    }
  }, [isUrlOwned, expanded, scrollToRef])

  useEffect(() => {
    if (controller !== CURRENT_AWARENESS) {
      setIsUrlOwned(nodeId === urlNode)
    }
  }, [controller, nodeId, urlNode, setIsUrlOwned])

  return (
    <div
      ref={scrollToRef}
      {...childProps}
      className={cx({ 'no-pointer-events': noPointerEvents })}
    >
      {showToggleExpandAllIcon && (
        <ExpandAllContentIconButton
          {...{ allContentExpanded, setAllContentExpanded, depth, parentTitle }}
        />
      )}
      <input
        type='checkbox'
        name={id}
        id={`${id}/${atBottom}`}
        checked={isExpanded}
        data-action={comingSoon ? undefined : 'expand'}
        data-path={id}
        data-type='group'
        data-value={isExpanded ? 0 : 1}
        readOnly
        onClick={handleOnClick}
      />
      <label
        style={{
          padding: labelPaddingStyle(depth),
          letterSpacing: '0.1px',
          boxShadow: 'none'
        }}
        className={cx(
          'w-100 f5 b min-h3 flex items-center ma0 bb',
          { 'flex-wrap': isMobile },
          { 'darker ': isExpanded && depth === 2 && !accessDenied },
          { 'bg-moon-gray o-60': accessDenied },
          {
            [depthStyle(!hasTitle && hasParentTitle ? depth - 1 : depth)]: !accessDenied,
            group: true,
            'group-header-archived': isArchived
          }
        )}
        htmlFor={`${id}/${atBottom}`}
        data-event-category={eventCategory}
        data-event-action={isExpanded ? 'Collapse' : 'Expand'}
        data-event-label={`[Group ${depth}] ${(title ?? '').replace(
          /(<([^>]+)>)/gi,
          ''
        )}`}
        {...(aol !== undefined && ({
          'data-event-aol': Array.isArray(aol) ? `[${aol.toString()}]` : aol
        }))}
      >
        {Prefix}
        <div
          className='flex items-center justify-center justify-between h-100 w-100'
        >
          <div className='flex items-center'>
            <span
              className={cx(
                'lh-title mr6',
                { mv4: depth > 1 },
                { underline: isUrlOwned }
              )}
            >
              {hasTitle ? title : hasParentTitle ? parentTitle : ''}
            </span>
            <HoverIconButtonContainer {...{ nodeId }} />
          </div>
          <div className='flex items-center justify-center'>
            {Suffix}
            {selected !== undefined && (
              <TealToolTip title={isSelected ? 'Deselect All' : 'Select All'} placement='top-end' arrow={false} bgColor='dark'>
                <div>
                  <Selector type='group' {...{ aol, atBottom, id, selected, title, expanded }} />
                </div>
              </TealToolTip>
            )}
            {comingSoon && (() => {
              const label = comingSoonText(aol, focusedId)
              return label !== null && label !== '' ? <span className='bg-cadet-blue white br-pill ph3 pv1 f7 nowrap mr4 no-pointer-events'>{label}</span> : null
            })()}
            {!isExpanded && depth === 1 && !showAllS1 && (
              <div className={cx('flex items-center', iconDepthStyle(depth))}>
                <ArrowRight className='w7' />
              </div>
            )}
            {!isExpanded && (depth > 1 || showAllS1) && (
              <div className={cx('flex items-center', iconDepthStyle(depth))}>
                <ChevronDown className='w7' />
              </div>
            )}
            {isExpanded && (depth > 1 || showAllS1) && (
              <div className={cx('flex items-center', iconDepthStyle(depth))}>
                <ChevronUp className='w7' />
              </div>
            )}
          </div>
        </div>
        {isArchived === true && <span className='archived-label'>Archived</span>}
      </label>
      {comingSoon && (
        <ComingSoonDialog
          open={showComingSoonDialog}
          onClose={() => setShowComingSoonDialog(false)}
        />
      )}
    </div>
  )
}

export default GroupHeader

const GroupHeaderPrefix: FunctionComponent<{ prefix: string }> = ({ prefix }) => {
  const [hover, setHover] = useState(false)
  // fits e.g "Schedule 1ZB"; longer prefixes truncate with a tooltip
  const maxCharLength = 12
  // not a `ttc` class: CSS would re-capitalize the truncated fragment's first letter
  const capitalizedPrefix = prefix.charAt(0).toUpperCase() + prefix.slice(1)

  return (
    <div
      className='nowrap mr6 w11-half shrink-0'
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {capitalizedPrefix.length > maxCharLength && (
        <AccordionHeaderToolTip open={hover} title={capitalizedPrefix} placement='top' arrow>
          <span>{ellipsis(capitalizedPrefix, maxCharLength, { side: 'start' })}</span>
        </AccordionHeaderToolTip>
      )}
      {capitalizedPrefix.length <= maxCharLength && capitalizedPrefix}
    </div>
  )
}

const ExpandAllContentIconButton: FunctionComponent<Partial<GroupHeaderProps>> = ({ allContentExpanded, setAllContentExpanded, depth = 0, parentTitle }) => {
  const isAllContentExpanded = Boolean(allContentExpanded)
  const hasParentTitle = isNonEmptyString(parentTitle)
  const handleOnClick = (): void => { setAllContentExpanded?.(!isAllContentExpanded) }
  const leftOffset = 0.5 + (depth - 2)

  return (
    <div style={{ paddingLeft: `${depth}rem` }}>
      <TealToolTip title={isAllContentExpanded ? 'Collapse All Answers' : 'Expand All Answers'} placement='top-start' leaveDelay={300}>
        <IconButton
          size='small'
          className={cx('outline-0 absolute top4', { 'cadet-blue': !hasParentTitle }, { black: hasParentTitle })}
          style={{ left: `${leftOffset}rem` }}
          onClick={handleOnClick}
        >
          {isAllContentExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
        </IconButton>
      </TealToolTip>
    </div>
  )
}

type GroupHeaderPropTypes = GroupHeaderContainerProps & Partial<GroupProps> & AccordionHeaderProps

interface GroupHeaderProps extends GroupHeaderPropTypes {
  aol?: string | string[]
  eventCategory: string
  nodeId: string
  focusedId?: string
  originalNode?: {
    id: string
    title: string
  }
  prefix: string
  suffix: string
  isArchived?: boolean
  comingSoon?: boolean
  allContentExpanded?: boolean
  setAllContentExpanded?: (value: boolean) => void
  cxNames: any
  title?: string
  parentTitle?: string
  display?: number
  group?: AccordionGroup[]
  hasAccess?: boolean
}
